/**
 * Term Container (piece 15): one clause per record, numbered Section.Sub,
 * assembled on a document at render time through a three-level override
 * cascade, Document first, then Client, then Global. The arithmetic is pure so
 * the cascade, the numbering and the source status are testable without a
 * database.
 */
import { documentName } from '../documents/documentNames';
import { escapeHtml, sanitizeRichText } from '../controls/richText';

export type TermSource = 'Imported' | 'Overridden' | 'Custom';
export type OverrideLevel = 'Client' | 'Document';

/**
 * 🔒 A section of the library (T227; decision 0702): the heading its clauses
 * print beneath, and the kinds they print on. Two active sections never share
 * a number.
 */
export interface TermSectionRow {
  id: string;
  sectionNumber: number;
  /** Blank on a section the migration made, until somebody names it. */
  title: string;
  documentKinds: readonly string[];
  sortOrder: number;
  isActive: boolean;
  /**
   * 🔒 The section a sub-section stands under (T273; decision 0771), one level
   * deep: a top-level section carries none, and a sub-section's own
   * `sectionNumber` is its place among its parent's clauses and sub-sections,
   * 3 in 4.3.
   */
  parentSectionId?: string | null;
}

export interface TermContainerRow {
  id: string;
  title: string;
  /** 🔒 The clause's words as sanitised rich text (decision 0704). */
  bodyRichText: string;
  /** 🔒 The section the clause belongs to (decision 0702); null on the Preamble. */
  sectionId?: string | null;
  /** The section's number, read through `sectionId` and never stored on the clause; zero on the Preamble. */
  sectionNumber: number;
  /** 🔒 The parent's number where the clause stands in a sub-section, 4 in 4.3.1 (decision 0771); absent or null otherwise. */
  parentSectionNumber?: number | null;
  /** The clause's order inside its section; zero on a heading row an import wrote before sections were rows. */
  subNumber: number;
  source: TermSource;
  isPreamble: boolean;
  sortOrder: number;
  isActive: boolean;
  /**
   * 🔒 The kinds the clause narrows its section to (decisions 0684, 0702).
   * Empty or absent inherits the section's; the Preamble names its own.
   */
  documentKinds?: readonly string[];
}

export interface TermOverrideRow {
  id: string;
  termContainerId: string;
  level: OverrideLevel;
  companyId?: string | null;
  personId?: string | null;
  overrideTargetType?: string | null;
  overrideTargetId?: string | null;
  title: string;
  /** 🔒 The override's words as sanitised rich text (decision 0704). */
  bodyRichText: string;
  reviewFlaggedAt?: Date | string | null;
}

/**
 * 🔒 The seven printed kinds a clause can name as its scope (T125; decision
 * 0684). A sheet of any other kind prints no clause, and each of the seven
 * carries its own `termsOverride?`.
 */
export const TERM_PRINTED_KINDS = ['Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'WorkOrder', 'ChangeOrder', 'CreditNote'] as const;
export type TermPrintedKind = (typeof TERM_PRINTED_KINDS)[number];

export function isTermPrintedKind(kind: string): kind is TermPrintedKind {
  return (TERM_PRINTED_KINDS as readonly string[]).includes(kind);
}

/** 🔨 A section and the Preamble name at least one kind, and only the seven; one that should print nowhere is removed instead (decision 0702). */
export function termScopeProblem(kinds: readonly string[] | null | undefined, what = 'a section'): string | null {
  if (!kinds || kinds.length === 0) return `${what} prints on at least one kind of document; remove it instead`;
  const stray = kinds.find((kind) => !isTermPrintedKind(kind));
  return stray ? `${stray} is not a document a clause prints on` : null;
}

/**
 * 🔒 The kinds a clause prints on (decision 0702): its section's, narrowed to
 * the clause's own where it names any, and never wider than the section's. The
 * Preamble, which belongs to no section, prints on the kinds it names.
 */
export function effectiveKinds(container: { documentKinds?: readonly string[] | undefined; isPreamble: boolean }, section: Pick<TermSectionRow, 'documentKinds'> | null | undefined): readonly string[] {
  const own = container.documentKinds && container.documentKinds.length > 0 ? container.documentKinds : null;
  if (container.isPreamble || !section) return own ?? TERM_PRINTED_KINDS;
  return own ? own.filter((kind) => section.documentKinds.includes(kind)) : section.documentKinds;
}

/**
 * 🔒 A section as its clauses read it (decision 0771): a sub-section prints on
 * the kinds it names that its parent names too, and nothing while its parent is
 * removed. Null where the section or its parent is no longer active, or unknown.
 */
export function sectionInForce(section: TermSectionRow | null | undefined, sectionOf: ReadonlyMap<string, TermSectionRow>): Pick<TermSectionRow, 'documentKinds'> | null {
  if (!section || !section.isActive) return null;
  if (!section.parentSectionId) return section;
  const parent = sectionOf.get(section.parentSectionId);
  if (!parent || !parent.isActive) return null;
  return { documentKinds: section.documentKinds.filter((kind) => parent.documentKinds.includes(kind)) };
}

/** Whether a clause prints on a sheet of this kind: a kind outside the seven prints none (decisions 0684, 0702). */
export function clauseInScope(container: { documentKinds?: readonly string[] | undefined; isPreamble?: boolean }, documentType: string | null | undefined, section: Pick<TermSectionRow, 'documentKinds'> | null = null): boolean {
  if (!documentType) return true;
  if (!isTermPrintedKind(documentType)) return false;
  return effectiveKinds({ documentKinds: container.documentKinds, isPreamble: container.isPreamble ?? false }, section).includes(documentType);
}

/** 🔨 A clause narrows its section's kinds and never widens them (decision 0702); empty inherits them. */
export function clauseNarrowingProblem(kinds: readonly string[] | null | undefined, sectionKinds: readonly string[]): string | null {
  if (!kinds || kinds.length === 0) return null;
  const stray = kinds.find((kind) => !isTermPrintedKind(kind));
  if (stray) return `${stray} is not a document a clause prints on`;
  const wider = kinds.find((kind) => !sectionKinds.includes(kind));
  return wider ? `a clause narrows its section's kinds and never widens them; its section does not print on ${wider}` : null;
}

/**
 * 🔒 A section's printed heading (decision 0702): its number, then its title
 * where it has one. A sub-section reads its parent's number, then its own, with
 * no closing point: *4.3 Payment schedule* (decision 0771).
 */
export function sectionHeading(section: Pick<TermSectionRow, 'sectionNumber' | 'title'>, parent: Pick<TermSectionRow, 'sectionNumber'> | null = null): string {
  if (parent) return `${parent.sectionNumber}.${section.sectionNumber} ${section.title.trim()}`.trim();
  return `${section.sectionNumber}. ${section.title.trim()}`.trim();
}

/**
 * 🔒 The next place inside a section (T273; decision 0771): one past the last of
 * its clauses and its sub-sections, which share the one run of numbers, so what
 * is added from a section's bar lands after the bar's last child.
 */
export function nextChildNumber(clauseNumbers: readonly number[], subSectionNumbers: readonly number[] = []): number {
  return Math.max(0, ...clauseNumbers, ...subSectionNumbers) + 1;
}

/** 🔨 A sub-section stands one level deep (decision 0771): under an active top-level section, never under a sub-section. */
export function subSectionProblem(parent: Pick<TermSectionRow, 'isActive' | 'parentSectionId'> | null | undefined): string | null {
  if (!parent || !parent.isActive) return 'the section a sub-section stands under is not in the library';
  return parent.parentSectionId ? 'a sub-section stands one level deep, under a section and never under another sub-section' : null;
}

/** The next free section number: one past the highest active top-level one. */
export function nextSectionNumber(sections: readonly Pick<TermSectionRow, 'sectionNumber' | 'isActive' | 'parentSectionId'>[]): number {
  return Math.max(0, ...sections.filter((s) => s.isActive && !s.parentSectionId).map((s) => s.sectionNumber)) + 1;
}

/** 🔒 The sections a removal takes (decision 0771): the section itself and, on a top-level one, every sub-section under it. */
export function sectionsRemovedWith(sections: readonly Pick<TermSectionRow, 'id' | 'parentSectionId'>[], sectionId: string): Set<string> {
  return new Set([sectionId, ...sections.filter((section) => section.parentSectionId === sectionId).map((section) => section.id)]);
}

/** What a removal is asked to take: one section with its clauses, or one clause (decision 0703). */
export type TermRemoval = { sectionId: string } | { clauseId: string };

/** The kinds any active clause prints on, the Preamble aside, reading each clause's section. */
function kindsInPrint(sections: readonly TermSectionRow[], clauses: readonly TermContainerRow[]): Set<string> {
  const sectionOf = new Map(sections.map((section) => [section.id, section]));
  const kinds = new Set<string>();
  for (const clause of clauses) {
    if (!clause.isActive || clause.isPreamble) continue;
    const scope = clause.sectionId ? sectionInForce(sectionOf.get(clause.sectionId), sectionOf) : null;
    if (clause.sectionId && !scope) continue;
    for (const kind of effectiveKinds(clause, scope)) kinds.add(kind);
  }
  return kinds;
}

/**
 * 🔨 Why a removal is refused, by name, or null where nothing depends on it
 * (T225; decision 0703). A removal taking a kind's last printed clause is
 * refused while that kind carries an active signature party, which accepts the
 * terms and would accept none, or while the Preamble prints on it, which would
 * introduce nothing. Removing the Preamble itself takes nothing anything reads.
 */
export function termRemovalRefusal(
  library: { sections: readonly TermSectionRow[]; clauses: readonly TermContainerRow[] },
  parties: readonly { documentType: string; partyLabel: string; isActive?: boolean }[],
  removal: TermRemoval,
): string | null {
  const taken = 'sectionId' in removal ? sectionsRemovedWith(library.sections, removal.sectionId) : new Set<string>();
  const removing = (clause: TermContainerRow) => ('clauseId' in removal ? clause.id === removal.clauseId : taken.has(clause.sectionId ?? ''));
  if ('clauseId' in removal && library.clauses.some((clause) => clause.id === removal.clauseId && clause.isPreamble)) return null;
  const before = kindsInPrint(library.sections, library.clauses);
  const sections = 'sectionId' in removal ? library.sections.filter((section) => !taken.has(section.id)) : library.sections;
  const after = kindsInPrint(sections, library.clauses.filter((clause) => !removing(clause)));
  const preamble = library.clauses.find((clause) => clause.isPreamble && clause.isActive) ?? null;
  for (const kind of TERM_PRINTED_KINDS) {
    if (!before.has(kind) || after.has(kind)) continue;
    const party = parties.find((candidate) => candidate.documentType === kind && candidate.isActive !== false);
    if (party) return `the ${party.partyLabel} signature on the ${documentName(kind)} accepts these terms, and would accept none; turn that party off under Signature Block first`;
    if (preamble && effectiveKinds(preamble, null).includes(kind)) return `the Preamble introduces these terms on the ${documentName(kind)}, and would introduce none; narrow or remove the Preamble first`;
  }
  return null;
}

/** 🔒 The documents a removal takes the words off, in the printed order (decision 0703). */
export function kindsLostByRemoval(library: { sections: readonly TermSectionRow[]; clauses: readonly TermContainerRow[] }, removal: TermRemoval): string[] {
  const sectionOf = new Map(library.sections.map((section) => [section.id, section]));
  const leaving = 'sectionId' in removal ? sectionsRemovedWith(library.sections, removal.sectionId) : new Set<string>();
  const taken = library.clauses.filter((clause) => clause.isActive && !clause.isPreamble && ('clauseId' in removal ? clause.id === removal.clauseId : leaving.has(clause.sectionId ?? '')));
  if ('sectionId' in removal && taken.length === 0) return [...(sectionInForce(sectionOf.get(removal.sectionId), sectionOf)?.documentKinds ?? [])];
  const kinds = new Set(taken.flatMap((clause) => [...effectiveKinds(clause, clause.sectionId ? sectionInForce(sectionOf.get(clause.sectionId), sectionOf) : null)]));
  if ('clauseId' in removal) {
    const preamble = library.clauses.find((clause) => clause.id === removal.clauseId && clause.isPreamble);
    if (preamble) return [...effectiveKinds(preamble, null)];
  }
  return TERM_PRINTED_KINDS.filter((kind) => kinds.has(kind));
}

/** One clause as a sheet prints it: its Section.Sub place, its title and its words. */
export interface PrintTermsClause {
  position: string;
  title: string;
  /** 🔒 Sanitised rich text (decision 0704). */
  body: string;
  /**
   * 🔒 Present on a sub-section alone (T273; decision 0771): its place and title
   * print as a heading and these clauses beneath it, in their own order among
   * the parent's clauses; its `body` is empty.
   */
  clauses?: PrintTermsClause[];
}

/** One section as a sheet prints it: the heading, then its clauses in order (decision 0702). */
export interface PrintTermsSection {
  heading: string;
  clauses: PrintTermsClause[];
  /** 🔒 True on a section a confidentiality role added, which prints after the set and after the document's own text (decision 1171); absent otherwise. */
  addition?: boolean;
}

/**
 * 🔒 The terms in force for one document (decisions 0684, 0702): the
 * document's own text where it carries one, and else the Preamble and the
 * sections in scope, each heading before its clauses.
 */
export interface PrintTerms {
  /** 🔒 The document's own terms, sanitised rich text (decision 0704). */
  override: string | null;
  /** 🔒 The Preamble, sanitised rich text. */
  preamble: string | null;
  sections: PrintTermsSection[];
  /**
   * 🔒 The line a sent document's terms lead with, *Terms as sent* and the day,
   * or the note that its copy predates the kept terms (T277; decision 0773).
   * Absent on a Draft's live terms and in every stored copy.
   */
  asSent?: string | null;
}

/** True where a document carries no terms at all, so nothing is printed for them. */
export function printTermsEmpty(terms: PrintTerms | null | undefined): boolean {
  return !terms || (terms.override === null && terms.preamble === null && terms.sections.every((section) => section.clauses.length === 0));
}

/**
 * 🔒 The terms one document prints (decisions 0684, 0702): its own override
 * where it carries one; else the Preamble, then each section in scope with its
 * heading before its clauses, as the cascade resolved them. 🔒 A clause a
 * confidentiality role added prints after the document's own text as well: no
 * own text removes it (decision 1171).
 */
export function termsForSheet(resolved: readonly (Pick<ResolvedTerm, 'container' | 'title' | 'body' | 'section'> & { parentSection?: TermSectionRow | null; addition?: boolean })[], termsOverride: string | null | undefined): PrintTerms {
  if (termsOverride && termsOverride.trim()) return { override: termsOverride.trim(), preamble: null, sections: termsForSheet(resolved.filter((term) => term.addition === true && !term.container.isPreamble), null).sections };
  let preamble: string | null = null;
  const sections: PrintTermsSection[] = [];
  const byId = new Map<string, PrintTermsSection>();
  const subById = new Map<string, PrintTermsClause>();
  for (const term of resolved) {
    if (term.container.isPreamble) {
      preamble = term.body;
      continue;
    }
    // 🔒 A clause in a sub-section prints under its parent's heading, then the sub-section's, in the parent's own order (decision 0771).
    const top = term.parentSection ?? term.section;
    const key = top?.id ?? `number-${term.container.parentSectionNumber ?? term.container.sectionNumber}`;
    let section = byId.get(key);
    if (!section) {
      section = { heading: top ? sectionHeading(top) : `${term.container.parentSectionNumber ?? term.container.sectionNumber}.`, clauses: [], ...(term.addition === true ? { addition: true } : {}) };
      byId.set(key, section);
      sections.push(section);
    }
    const clause: PrintTermsClause = { position: positionLabel(term.container), title: term.title, body: term.body };
    if (term.parentSection && term.section) {
      let sub = subById.get(term.section.id);
      if (!sub) {
        sub = { position: `${term.parentSection.sectionNumber}.${term.section.sectionNumber}`, title: term.section.title.trim(), body: '', clauses: [] };
        subById.set(term.section.id, sub);
        section.clauses.push(sub);
      }
      sub.clauses!.push(clause);
    } else section.clauses.push(clause);
  }
  return { override: null, preamble, sections };
}

/**
 * The terms rebuilt from the Terms window's own list (decisions 0684, 0702):
 * the Preamble is the row with no heading and no place, and each run of rows
 * under one heading is one section.
 */
export function termsFromClauses(clauses: readonly { position: string; title: string; body: string; heading: string | null; subHeading?: string | null }[]): PrintTerms {
  let preamble: string | null = null;
  const sections: PrintTermsSection[] = [];
  for (const clause of clauses) {
    if (clause.heading === null && clause.position === '') {
      preamble = clause.body;
      continue;
    }
    const heading = clause.heading ?? '';
    const last = sections[sections.length - 1];
    const section = last && last.heading === heading ? last : { heading, clauses: [] };
    if (section !== last) sections.push(section);
    const own = { position: clause.position, title: clause.title, body: clause.body };
    // 🔒 A run of rows under one sub-section heading is one sub-section (decision 0771); its heading reads place, then title.
    if (clause.subHeading) {
      const tail = section.clauses[section.clauses.length - 1];
      const heads = (sub: PrintTermsClause) => `${sub.position} ${sub.title}`.trim() === clause.subHeading;
      const sub = tail?.clauses && heads(tail) ? tail : { position: clause.subHeading.split(' ')[0] ?? '', title: clause.subHeading.split(' ').slice(1).join(' '), body: '', clauses: [] };
      if (sub !== tail) section.clauses.push(sub);
      sub.clauses!.push(own);
    } else section.clauses.push(own);
  }
  return { override: null, preamble, sections };
}

/**
 * 🔒 The terms as one piece of rich text, each heading before its clauses: what
 * a person replacing them for one document starts from (decisions 0684, 0704).
 * Every part is escaped or sanitised, so the answer is safe to hand the editor.
 */
export function termsRichText(terms: PrintTerms): string {
  if (terms.override !== null) return sanitizeRichText(terms.override);
  const escape = escapeHtml;
  const parts: string[] = terms.preamble ? [sanitizeRichText(terms.preamble)] : [];
  const clauseParts = (clause: PrintTermsClause): string[] =>
    clause.clauses
      ? [`<h3>${escape(`${clause.position} ${clause.title}`.trim())}</h3>`, ...clause.clauses.flatMap(clauseParts)]
      : [`<p><strong>${escape(`${clause.position} ${clause.title}`.trim())}</strong></p>`, sanitizeRichText(clause.body)];
  for (const section of terms.sections) {
    parts.push(`<h2>${escape(section.heading)}</h2>`);
    for (const clause of section.clauses) parts.push(...clauseParts(clause));
  }
  return parts.join('');
}

/** The document types a Term Container assembles on; a Document override points at one of these (piece 15). */
export const TERM_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'PurchaseOrder', 'Invoice'] as const;
export type TermDocumentType = (typeof TERM_DOCUMENT_TYPES)[number];

export interface RenderContext {
  companyId?: string | null;
  personId?: string | null;
  documentType?: TermDocumentType | string | null;
  documentId?: string | null;
}

export interface ResolvedTerm {
  container: TermContainerRow;
  /** 🔒 The section the clause prints beneath (decision 0702); null on the Preamble. */
  section: TermSectionRow | null;
  /** 🔒 The section a sub-section stands under, where the clause's section is one (decision 0771); null otherwise. */
  parentSection: TermSectionRow | null;
  title: string;
  body: string;
  /** Which level answered: Global where no override applies. */
  level: 'Global' | OverrideLevel;
  overrideId: string | null;
}

/** "4.3" from the two-level position, "4.3.1" in a sub-section (decision 0771); a Section row reads "4."; the Preamble reads nothing. */
export function positionLabel(row: Pick<TermContainerRow, 'sectionNumber' | 'subNumber' | 'isPreamble' | 'parentSectionNumber'>): string {
  if (row.isPreamble) return '';
  if (row.parentSectionNumber) return `${row.parentSectionNumber}.${row.sectionNumber}.${row.subNumber}`;
  return row.subNumber === 0 ? `${row.sectionNumber}.` : `${row.sectionNumber}.${row.subNumber}`;
}

/** The two-level position from typed text: "4.3" → 4 and 3, "4" → the Section row; anything else is refused. */
export function parsePosition(text: string): { sectionNumber: number; subNumber: number } | null {
  const match = /^\s*(\d+)(?:\.(\d+))?\.?\s*$/.exec(text);
  if (!match) return null;
  const sectionNumber = Number(match[1]);
  const subNumber = match[2] === undefined ? 0 : Number(match[2]);
  if (sectionNumber < 1 || subNumber < 0) return null;
  return { sectionNumber, subNumber };
}

/** 🔨 The Preamble leads, then every clause in Section.Sub order; imported numbering is kept and a new clause slots in without renumbering. */
export function orderContainers<T extends Pick<TermContainerRow, 'sectionNumber' | 'subNumber' | 'isPreamble' | 'sortOrder' | 'parentSectionNumber'>>(rows: readonly T[]): T[] {
  // 🔒 A clause in sub-section 4.3 sorts as 4, 3, then its own place, among section 4's own clauses (decision 0771).
  const key = (row: T): [number, number, number] => (row.parentSectionNumber ? [row.parentSectionNumber, row.sectionNumber, row.subNumber] : [row.sectionNumber, row.subNumber, 0]);
  return [...rows].sort((a, b) => {
    if (a.isPreamble !== b.isPreamble) return a.isPreamble ? -1 : 1;
    const [x, y] = [key(a), key(b)];
    return x[0] - y[0] || x[1] - y[1] || x[2] - y[2] || a.sortOrder - b.sortOrder;
  });
}

/** The next free sub-clause under a Section: 4.7 after an imported 4.1 to 4.6. */
export function nextSubNumber(rows: readonly Pick<TermContainerRow, 'sectionNumber' | 'subNumber' | 'isPreamble'>[], sectionNumber: number): number {
  const taken = rows.filter((r) => !r.isPreamble && r.sectionNumber === sectionNumber).map((r) => r.subNumber);
  return taken.length === 0 ? 1 : Math.max(...taken) + 1;
}

/** 🔨 Overridden is written by the edit action, never by hand: an Imported clause edited becomes Overridden; Custom stays Custom. */
export function sourceAfterEdit(source: TermSource, changed: boolean): TermSource {
  if (!changed) return source;
  return source === 'Imported' ? 'Overridden' : source;
}

export function assertSourceNotSetByHand(patch: Record<string, unknown>): void {
  if ('source' in patch) throw new Error('a clause source is written by the edit action and never set by hand');
}

function matchesClient(override: TermOverrideRow, ctx: RenderContext): boolean {
  if (override.level !== 'Client') return false;
  if (override.companyId && ctx.companyId && override.companyId === ctx.companyId) return true;
  return Boolean(override.personId && ctx.personId && override.personId === ctx.personId);
}

function matchesDocument(override: TermOverrideRow, ctx: RenderContext): boolean {
  return override.level === 'Document' && Boolean(ctx.documentType && ctx.documentId) && override.overrideTargetType === ctx.documentType && override.overrideTargetId === ctx.documentId;
}

/**
 * 🔨 Resolution at render: Document override first, then Client, then the
 * Global container itself. A Document override always wins over a Client one
 * for the same container, even where both exist. The Preamble resolves through
 * the same cascade and stays the list's own first row.
 */
export function resolveTerms(containers: readonly TermContainerRow[], overrides: readonly TermOverrideRow[], ctx: RenderContext, sections: readonly TermSectionRow[]): ResolvedTerm[] {
  const sectionOf = new Map(sections.map((section) => [section.id, section]));
  // 🔒 A clause in a section no longer active prints nowhere; one whose section is not known reads its own kinds (decision 0702).
  // 🔒 A sub-section prints on its parent's kinds and never wider, and nothing once its parent is removed (decision 0771).
  const printable = containers.filter((c) => {
    if (!c.isActive) return false;
    const section = c.sectionId ? sectionOf.get(c.sectionId) : undefined;
    if (c.sectionId && section && !sectionInForce(section, sectionOf)) return false;
    return clauseInScope(c, ctx.documentType, section ? sectionInForce(section, sectionOf) : null);
  });
  // 🔒 Only the clauses naming the sheet's own kind, in their Section.Sub order (decision 0684).
  return orderContainers(printable).map((container) => {
    const section = container.sectionId ? (sectionOf.get(container.sectionId) ?? null) : null;
    const parentSection = section?.parentSectionId ? (sectionOf.get(section.parentSectionId) ?? null) : null;
    const own = overrides.filter((o) => o.termContainerId === container.id);
    const document = own.find((o) => matchesDocument(o, ctx)) ?? null;
    const client = own.find((o) => matchesClient(o, ctx)) ?? null;
    const winner = document ?? client;
    if (!winner) return { container, section, parentSection, title: container.title, body: container.bodyRichText, level: 'Global', overrideId: null };
    return { container, section, parentSection, title: winner.title, body: winner.bodyRichText, level: winner.level, overrideId: winner.id };
  });
}

/** Every override still pointing at a Global container, which an edit to that container flags for review. */
export function overridesToFlag(overrides: readonly TermOverrideRow[], termContainerId: string): TermOverrideRow[] {
  return overrides.filter((o) => o.termContainerId === termContainerId);
}

/** One section the tree draws, with its clauses beneath it in order (decision 0702). */
export interface TermSectionGroup {
  section: TermSectionRow;
  clauses: TermContainerRow[];
  /**
   * 🔒 Every child in its place (decision 0771): the section's own clauses and
   * its sub-sections, each sub-section with its clauses, in the one run of
   * numbers they share. A sub-section's group carries none.
   */
  children: ({ clause: TermContainerRow } | { subSection: TermSectionGroup })[];
}

/** The library as the tree draws it: the Preamble, then every active top-level section in number order with its clauses and sub-sections in place. */
export function sectionsOf(sections: readonly TermSectionRow[], rows: readonly TermContainerRow[]): { preamble: TermContainerRow | null; sections: TermSectionGroup[] } {
  const ordered = orderContainers(rows.filter((r) => r.isActive));
  const preamble = ordered.find((r) => r.isPreamble) ?? null;
  const active = [...sections].filter((section) => section.isActive).sort((a, b) => a.sectionNumber - b.sectionNumber || a.sortOrder - b.sortOrder);
  const clausesIn = (section: TermSectionRow) => ordered.filter((row) => !row.isPreamble && row.sectionId === section.id);
  const groups = active
    .filter((section) => !section.parentSectionId)
    .map((section): TermSectionGroup => {
      const clauses = clausesIn(section);
      const subs = active.filter((sub) => sub.parentSectionId === section.id).map((sub): TermSectionGroup => ({ section: sub, clauses: clausesIn(sub), children: [] }));
      const children = [
        ...clauses.map((clause) => ({ place: clause.subNumber, child: { clause } as const })),
        ...subs.map((sub) => ({ place: sub.section.sectionNumber, child: { subSection: sub } as const })),
      ]
        .sort((a, b) => a.place - b.place)
        .map((entry) => entry.child);
      return { section, clauses, children };
    });
  return { preamble, sections: groups };
}

/** What the parser seam returns: a preamble, an ordered clause list and a signature block, each surfaced separately at import review. */
export interface ParsedClause {
  sectionNumber: number;
  subNumber: number;
  title: string;
  body: string;
}

/**
 * One paragraph of a source document: its text, and the leading run of bold
 * words when the reader could tell (T2200). A paragraph whose whole text is bold
 * carries no lead.
 */
export interface ClauseParagraph {
  text: string;
  boldLead?: string;
}

export interface ParsedClauseDocument {
  preamble: string | null;
  clauses: ParsedClause[];
  signatureBlock: string | null;
}

/**
 * 🔒 A clause's title and body from the words after its number (T2200), in
 * this order: the leading bold run, its closing period dropped; the part before
 * a spaced dash; the first sentence when it ends in a period inside eighty
 * characters; otherwise no title and the whole text as the body.
 */
function clauseTitleAndBody(rest: string, boldLead: string, ownNumber: string): { title: string; body: string } {
  // 🔨 Only the clause's own number is taken off the front of the bold run (T2215), so a bold
  // title that opens on a digit of its own, such as "30-Day Terms.", keeps it.
  // The number is the one written in the text, "3.01" or "03.1" as typed, and never rebuilt from its parsed digits (T2308).
  const own = boldLead.startsWith(ownNumber) && /^(?:\.|\s|$)/.test(boldLead.slice(ownNumber.length));
  const lead = (own ? boldLead.slice(ownNumber.length).replace(/^\.?\s*/, '') : boldLead).trim();
  if (lead && lead.length < rest.length && rest.startsWith(lead)) {
    return { title: lead.replace(/\.$/, '').trim(), body: rest.slice(lead.length).trim() };
  }
  const split = /^(.{1,80}?)\s+[—–-]\s+(.+)$/.exec(rest);
  if (split) return { title: split[1]!, body: split[2]! };
  const sentence = /^(.{1,79}?)\.(?:\s+(.+))?$/s.exec(rest);
  if (sentence) return { title: sentence[1]!, body: sentence[2] ?? '' };
  return { title: '', body: rest };
}

/**
 * The outline an import review shows: the clauses found, and whether the
 * source carried numbering at all. 🔨 A source with no detectable numbering
 * stops at the review screen with whatever was found, and nothing is written.
 */
export function importOutline(parsed: ParsedClauseDocument): { numbered: boolean; sections: number; clauses: number; hasPreamble: boolean; hasSignatureBlock: boolean } {
  const sections = new Set(parsed.clauses.map((c) => c.sectionNumber));
  return { numbered: parsed.clauses.length > 0, sections: sections.size, clauses: parsed.clauses.filter((c) => c.subNumber > 0).length, hasPreamble: parsed.preamble !== null, hasSignatureBlock: parsed.signatureBlock !== null };
}

/**
 * The outline from plain paragraphs, one per line: a heading "4. Pricing" opens
 * a Section, "4.3 Payment Terms — Net 30 …" is a clause, the unnumbered text
 * before Section 1 is the preamble, and a closing block naming signature lines
 * is the signature block. A clause spanning a break is joined on its own
 * number rather than split at the break.
 */
export function outlineFromParagraphs(paragraphs: readonly (string | ClauseParagraph)[]): ParsedClauseDocument {
  const clauses: ParsedClause[] = [];
  const preamble: string[] = [];
  const signature: string[] = [];
  let inSignature = false;
  let last: ParsedClause | null = null;
  for (const paragraph of paragraphs) {
    const text = (typeof paragraph === 'string' ? paragraph : paragraph.text).trim();
    const boldLead = typeof paragraph === 'string' ? '' : (paragraph.boldLead ?? '').trim();
    if (!text) continue;
    if (/^(signature block|signatures?|in witness whereof|agreed and accepted)\b/i.test(text) || (inSignature && /\b(by|printed name|date)\s*:/i.test(text))) {
      inSignature = true;
      signature.push(text);
      continue;
    }
    if (inSignature) {
      signature.push(text);
      continue;
    }
    const numbered = /^(\d+)(?:\.(\d+))?\.?\s+(.+)$/.exec(text);
    if (numbered) {
      const sectionNumber = Number(numbered[1]);
      const subNumber = numbered[2] === undefined ? 0 : Number(numbered[2]);
      const rest = numbered[3]!;
      const { title, body } = subNumber === 0 ? { title: rest, body: '' } : clauseTitleAndBody(rest, boldLead, `${numbered[1]}.${numbered[2]}`);
      last = { sectionNumber, subNumber, title: title.trim(), body: body.trim() };
      clauses.push(last);
      continue;
    }
    if (last && last.subNumber > 0) {
      last.body = last.body ? `${last.body} ${text}` : text;
      continue;
    }
    if (clauses.length === 0) preamble.push(text);
    else if (last) last.body = last.body ? `${last.body} ${text}` : text;
  }
  return { preamble: preamble.length > 0 ? preamble.join(' ') : null, clauses, signatureBlock: signature.length > 0 ? signature.join('\n') : null };
}
